import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ShoppingBag, Minus, Plus, Trash2, Loader2, ArrowRight } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { formatPrice } from "@/lib/shopify";

export function CartDrawer() {
  const [open, setOpen] = useState(false);
  const {
    items,
    isLoading,
    isSyncing,
    updateQuantity,
    removeItem,
    getCheckoutUrl,
    syncCart,
  } = useCartStore();

  const totalItems = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = items.reduce(
    (s, i) => s + parseFloat(i.price.amount) * i.quantity,
    0,
  );
  const currency = items[0]?.price.currencyCode ?? "USD";

  useEffect(() => {
    if (open) syncCart();
  }, [open, syncCart]);

  const handleCheckout = () => {
    const url = getCheckoutUrl();
    if (url) {
      window.open(url, "_blank");
      setOpen(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="relative flex h-10 w-10 items-center justify-center text-foreground transition-opacity hover:opacity-70"
          aria-label="Cart"
        >
          <ShoppingBag className="h-5 w-5" strokeWidth={1.25} />
          {totalItems > 0 && (
            <Badge className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-medium text-background">
              {totalItems}
            </Badge>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col bg-background sm:max-w-md">
        <SheetHeader className="border-b border-hairline pb-4">
          <SheetTitle className="font-serif text-2xl font-light">Your Selection</SheetTitle>
          <SheetDescription className="text-xs uppercase tracking-luxe text-muted-foreground">
            {totalItems === 0
              ? "No stones reserved"
              : `${totalItems} stone${totalItems !== 1 ? "s" : ""} reserved`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col pt-6">
          {items.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <ShoppingBag
                className="mb-6 h-10 w-10 text-muted-foreground"
                strokeWidth={1}
              />
              <p className="font-serif text-xl">Your selection awaits.</p>
              <p className="mt-2 max-w-xs text-sm text-muted-foreground">
                Browse the collection to reserve a stone.
              </p>
            </div>
          ) : (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto pr-2">
                <div className="space-y-6">
                  {items.map((item) => (
                    <div
                      key={item.variantId}
                      className="flex gap-4 border-b border-hairline pb-6"
                    >
                      <div className="h-20 w-20 flex-shrink-0 overflow-hidden bg-secondary">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img
                            src={item.product.node.images.edges[0].node.url}
                            alt={item.product.node.title}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-serif text-lg leading-tight">
                            {item.product.node.title}
                          </h4>
                          <button
                            onClick={() => removeItem(item.variantId)}
                            className="text-muted-foreground transition-colors hover:text-foreground"
                            aria-label="Remove"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.25} />
                          </button>
                        </div>
                        {item.selectedOptions.length > 0 && (
                          <p className="mt-1 text-xs uppercase tracking-luxe text-muted-foreground">
                            {item.selectedOptions.map((o) => o.value).join(" · ")}
                          </p>
                        )}
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center border border-hairline">
                            <button
                              onClick={() =>
                                updateQuantity(item.variantId, item.quantity - 1)
                              }
                              className="flex h-7 w-7 items-center justify-center transition-colors hover:bg-secondary"
                              aria-label="Decrease"
                            >
                              <Minus className="h-3 w-3" strokeWidth={1.25} />
                            </button>
                            <span className="w-8 text-center text-sm">{item.quantity}</span>
                            <button
                              onClick={() =>
                                updateQuantity(item.variantId, item.quantity + 1)
                              }
                              className="flex h-7 w-7 items-center justify-center transition-colors hover:bg-secondary"
                              aria-label="Increase"
                            >
                              <Plus className="h-3 w-3" strokeWidth={1.25} />
                            </button>
                          </div>
                          <span className="font-serif text-lg">
                            {formatPrice(
                              parseFloat(item.price.amount) * item.quantity,
                              item.price.currencyCode,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 space-y-4 border-t border-hairline bg-background pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-luxe text-muted-foreground">
                    Subtotal
                  </span>
                  <span className="font-serif text-2xl">
                    {formatPrice(totalPrice, currency)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Taxes and shipping calculated at checkout.
                </p>
                <Button
                  onClick={handleCheckout}
                  disabled={isLoading || isSyncing}
                  className="h-12 w-full rounded-none bg-foreground text-background hover:bg-foreground/90"
                >
                  {isLoading || isSyncing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2 text-xs uppercase tracking-luxe">
                      Proceed to Checkout
                      <ArrowRight className="h-3 w-3" strokeWidth={1.25} />
                    </span>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
